<template>
  <v-card
    class="my-2 px-4 py-3 v-card-customer"
    :to="'/customers/' + customer.id"
    :color="colour"
    style="border-radius: 0.75rem"
    :flat="customer.closed"
    :class="{ 'lighten-3': customer.closed }"
  >
    <v-layout fill-height>
      <v-flex xs4>
        <v-layout column fill-height justify-space-between :class="{ 'text--secondary': customer.closed }">
          <div>
            <div
              class="subtitle-1"
              style="font-weight: 600;line-height: 1;text-transform: uppercase;letter-spacing:0.015em"
            >
              {{ customer.name }}
            </div>
            <span class="subtitle-1">{{ customer.funding | currency }}</span>
          </div>
          <div v-if="customer.users">
            <div class="body-2" style="line-height: 1;text-transform: uppercase;letter-spacing:0.015em">
              {{ customer.users[0].name.first }} {{ customer.users[0].name.last }}
            </div>
            <div class="body-1" style="line-height:1">
              {{ customer.users[0].phone }}
            </div>
          </div>
        </v-layout>
      </v-flex>
      <v-flex xs5>
        <v-layout column fill-height>
          <v-layout v-if="UserHasRole('Platform')" justify-space-between>
            <span class="caption text--secondary text--darken-2 mb-2 pl-2" v-if="customer.referral">
              <strong>Source:</strong> {{ customer.referrer.name }}
            </span>
            <span class="caption text--secondary text--darken-2 mb-2 pl-2" v-else>
              <strong>Source:</strong> Organic
            </span>
            <span class="caption text--secondary text--darken-2 mb-2 pl-2">
              <strong>Active Journey:</strong> {{ customer.active_journey.name }}
            </span>
          </v-layout>
          <div v-if="customer.enquiries && customer.enquiries.length > 0" style="min-height: 64px">
            <enquiry-status-chip
              v-for="enquiry in customer.enquiries"
              :key="enquiry.id"
              :product="enquiry.product.name"
              :status="enquiry.status.name"
              :disabled="customer.closed"
              :revolving="enquiry.revolving"
            ></enquiry-status-chip>
          </div>
          <div v-else style="min-height: 64px">
            <v-chip small class="lighten-2" style="width:72px;opacity:0.6" v-for="n in 6" :key="n"></v-chip>
          </div>
        </v-layout>
      </v-flex>
      <v-flex>
        <v-layout column align-end fill-height justify-space-between>
          <div class="caption text--secondary">
            {{ [customer.created_at, 'YYYY-MM-DD H:mm:ss'] | moment('calendar') }}
          </div>
          <div>
            <v-icon
              size="20"
              class="pl-1"
              v-if="UIFilterOptions.referral && customer.referral && !customer.self_referred"
              color="success"
              >fad fa-people-carry
            </v-icon>
            <v-icon
              size="24"
              class="pl-1"
              v-if="UIFilterOptions.hasEnquiryErrors && customer.has_enquiry_errors"
              color="error"
              >fad fa-bug
            </v-icon>
            <v-icon
              size="24"
              class="pl-1"
              v-if="UIFilterOptions.hasEnquiryErrors && customer.has_enquiry_feedback"
              color="warning"
              >fad fa-person-sign
            </v-icon>
            <v-icon
              size="20"
              class="pl-1"
              v-if="UIFilterOptions.hasEnquiryErrors && customer.has_enquiry_quote"
              color="success"
              >fad fa-envelope-open-text
            </v-icon>
            <v-icon size="20" class="pl-1" v-if="UIFilterOptions.referral && customer.self_referred">contacts</v-icon>
            <v-icon size="20" class="pl-1" v-if="UIFilterOptions.toBeCalled && customer.to_be_called" color="error"
              >fad fa-phone-plus
            </v-icon>
            <v-icon
              size="20"
              class="pl-1"
              v-if="UIFilterOptions.hotProspect && customer.hot_prospect"
              style="--fa-primary-color: #FF5252; --fa-secondary-color: #FFC107; --fa-secondary-opacity: 1.0;"
              >fad fa-flame
            </v-icon>
            <v-icon size="20" class="pl-1" v-if="customer.complaint_raised" color="error"
              >fad fa-exclamation-triangle
            </v-icon>
            <v-icon size="20" class="pl-1" v-if="UIFilterOptions.trustPilot && customer.trustpilot" color="warning"
              >fa-star
            </v-icon>
            <v-icon
              size="20"
              class="pl-1"
              v-if="UIFilterOptions.financialsSent && customer.financials_sent"
              color="warning"
              >fad fa-envelope-open-dollar
            </v-icon>
            <v-icon
              size="20"
              class="pl-1"
              v-if="UIFilterOptions.autoSendEnquiries && customer.auto_send_enquiries"
              color="purple"
            >
              fad fa-shipping-fast
            </v-icon>
            <v-icon
              size="20"
              class="pl-1"
              v-if="UIFilterOptions.softSearchConsent && customer.soft_search_consent"
              color="purple"
            >
              fad fa-thumbs-up
            </v-icon>
            <v-icon
              size="20"
              class="pl-1"
              v-if="UIFilterOptions.hasDocuments && customer.has_documents"
              color="success"
            >
              fad fa-file-alt
            </v-icon>
          </div>
          <v-layout align-center>
            <v-icon
              size="24"
              class="pr-2"
              v-if="UIFilterOptions.referral && customer.referrer.type === 'broker'"
              color="success"
              >fad fa-user-chart
            </v-icon>
            <v-icon
              size="24"
              class="pr-2"
              v-if="UIFilterOptions.referral && customer.referrer.type === 'bank'"
              color="success"
              >fad fa-university
            </v-icon>
            <v-icon
              size="24"
              class="pr-2"
              v-if="UIFilterOptions.referral && customer.referrer.type === 'partner'"
              color="success"
              >fad fa-handshake
            </v-icon>
            <abf-question-completion-gauge
              :question_completion_percentage="customer.question_completion_percentage"
            ></abf-question-completion-gauge>
            <abf-owner-select :owners="OwnersList" :selected="customer.owner.id" @update:selected="changeOwner" />
          </v-layout>
        </v-layout>
      </v-flex>
    </v-layout>
  </v-card>
</template>

<script>
import _ from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import EnquiryStatusChip from './Widgets/EnquiryStatusChip';
import AbfQuestionCompletionGauge from '../../Abf/AbfQuestionCompletionGauge';
import AbfOwnerSelect from '../../Abf/AbfOwnerSelect';

export default {
  name: 'CustomerCard',
  components: { AbfOwnerSelect, EnquiryStatusChip, AbfQuestionCompletionGauge },
  props: ['customer', 'selectedCustomerId'],
  data: () => ({
    tabs: null
  }),
  computed: {
    ...mapGetters(['OwnersList', 'PlatformUser', 'UserHasRole', 'UIFilterOptions', 'UILeftDrawer']),
    progressColour() {
      let colour = 'red';
      if (this.customer.question_completion_percentage === 100) {
        colour = 'accent';
      } else if (this.customer.question_completion_percentage >= 50) {
        colour = 'orange darken-2';
      }
      return colour;
    },
    active() {
      return this.customer.id === this.selectedCustomerId;
    },
    inactive() {
      return this.selectedCustomerId !== null && !this.active;
    },
    avatarSrc() {
      return this.customer.owner.id !== null && this.isPlatformUser(this.customer.owner.id)
        ? `https://s3-eu-west-1.amazonaws.com/media.alternativebusinessfunding.co.uk/public/avatars/${this.customer.owner.id}.png`
        : 'https://s3-eu-west-1.amazonaws.com/media.alternativebusinessfunding.co.uk/public/avatars/fa4b47dd-c7ee-4f80-9fac-b389588ba226.png';
    },
    colour() {
      return this.customer.closed ? 'grey lighten-3' : 'white';
    }
  },
  methods: {
    ...mapActions(['getOwnersList', 'updateCustomer']),
    selectCustomer() {
      this.$emit('update:selectedCustomerId', this.customer.id);
    },
    deselectCustomer() {
      this.$emit('update:selectedCustomerId', null);
    },
    isPlatformUser(id) {
      return _.find(this.OwnersList, { id });
    },
    changeOwner(ownerId) {
      this.updateCustomer({
        id: this.customer.id,
        updates: {
          owner_id: ownerId
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.v-card-customer {
  transition: flex 600ms cubic-bezier(0.19, 1, 0.22, 1);

  .inactive {
    opacity: 0.2;
  }

  &.v-card-customer--active {
    /*position: relative;*/
    /*top: 0;*/
    /*left: 0;*/
    /*height: 100vh;*/
    /*width: 100%;*/
  }
}
</style>
